import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import {
  Button, Form, Header, Input, Loader, Table,
  TableBody, TableCell, TableFooter,
  TableHeader, TableHeaderCell, TableRow
} from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

interface HCHours {
  workid: number;
  date: string;
  cluster_Name: string;
  emp_id: string;
  emp_Name: string;
  working_hours: string;
  extra_hours: string;
  title_code: string;
  action_status: string;
}

const ManualHoursAdd = () => {
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [loadData, setLoadData] = useState<HCHours[]>([]);
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [wrkhr, setWrkhr] = useState('00:00');
  const [exthr, setExthr] = useState('00:00');
  const [empId, setEmpId] = useState('');
  const [selectedData, setSelectedData] = useState<HCHours | null>(null);

  const updateEnable = Boolean(selectedData);

  useEffect(() => {
    const empId = searchParams.get('employeeId') || '';
    const date = searchParams.get('selectedDate') || '';
    setEmpId(empId);
    setFromDate(date ? new Date(date) : new Date());
  }, [searchParams]);

  const fetchData = async () => {
    if (!empId) {
      toast.error('Please enter Employee ID');
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get("/Hour/EmployeeWorkingHoursDetails", {
        params: {
          dateTime: moment(fromDate).format('YYYY-MM-DD'),
          EmployeeId: empId,
        }
      });
      setLoadData(data);
    } catch (error) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'response' in error &&
        (error as any).response &&
        (error as any).response.status === 404 &&
        (error as any).response.data === 'No data found'
      ) {
        setLoadData([]);
        toast.warning('No data available for selected date');
      } else {
        toast.error("Failed to fetch data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (data: HCHours) => {
    setSelectedData(data);
    setWrkhr(data.working_hours);
    setExthr(data.extra_hours);
  };

  const handleDateChange = (date: Date) => {
    setFromDate(date);
  };

  const handleUpdate = async () => {
    if (!selectedData) return;
    
    if (!wrkhr || !exthr) {
      toast.error('Please enter valid hours');
      return;
    }

    try {
      const { data: message } = await axios.put(`/Hour/EmployeeWorkingManualUpdate`, null, {
        params: {
          Workid: selectedData.workid,
          Time: wrkhr || '00:00',
          ExtraHours: exthr || '00:00',
        }
      });
      toast.success(message);
      setSelectedData(null);
      setWrkhr('00:00');
      setExthr('00:00');
      fetchData();
    } catch {
      toast.error('Update failed.');
    }
  };

  const calculateTotals = useCallback(() => {
    const toMinutes = (time: string | null | undefined): number => {
      if (!time) return 0;
      const [h, m] = time.split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    };

    const toTime = (minutes: number): string =>
      `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

    const totalWorkMins = loadData.reduce((sum, item) => sum + toMinutes(item.working_hours), 0);
    const totalExtraMins = loadData.reduce((sum, item) => sum + toMinutes(item.extra_hours), 0);

    return {
      totalWorkingHours: toTime(totalWorkMins),
      totalExtraHours: toTime(totalExtraMins),
    };
}, [loadData]);

  const { totalWorkingHours, totalExtraHours } = calculateTotals();

  return (
    <div className='title_wise_cutlistreport'>
      <Header as="h2">Hour Update</Header>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginTop: 20 }}>
        <label>Date</label>
        <DatePicker
          selected={fromDate}
          onChange={handleDateChange}
          className="date-picker-style"
        />
        <label>Employee Id</label>
        <Input value={empId} onChange={(e) => setEmpId(e.target.value)} />
        <Button primary onClick={fetchData}>Submit</Button>
      </div>

      {updateEnable && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 20 }}>
          <Form.Field>
            <label>Working Hours </label>
            <TimePicker
              onChange={(value) => setWrkhr(value ?? '00:00')}
              value={wrkhr || '00:00'}
              format="HH:mm"
              maxTime="08:00"
              disableClock={true}
              className="time-picker-style"
            />
          </Form.Field>

          <Form.Field>
            <label>Extra Hours </label>
            <TimePicker
              onChange={(value) => setExthr(value ?? '00:00')}
              value={exthr || '00:00'}
              format="HH:mm"
              maxTime="08:00"
              disableClock={true}
              className="time-picker-style"
            />
          </Form.Field>
          <Button primary style={{ marginTop: '15px'}} onClick={handleUpdate}>Update</Button>
        </div>
      )}

      <div style={{ maxHeight: '75vh', overflowY: 'auto', marginTop: '100px' }}>
        <Table celled>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Date</TableHeaderCell>
              <TableHeaderCell>Id</TableHeaderCell>
              <TableHeaderCell>Cluster Name</TableHeaderCell>
              <TableHeaderCell>Emp Id</TableHeaderCell>
              <TableHeaderCell>Emp Name</TableHeaderCell>
              <TableHeaderCell>Working Hours</TableHeaderCell>
              <TableHeaderCell>Extra Hours</TableHeaderCell>
              <TableHeaderCell>Title Code</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Modify</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {!loading && loadData.length > 0 ? (
              loadData.map((data, idx) => (
                <TableRow key={idx}>
                  <TableCell>{data.date}</TableCell>
                  <TableCell>{data.workid}</TableCell>
                  <TableCell>{data.cluster_Name}</TableCell>
                  <TableCell>{data.emp_id}</TableCell>
                  <TableCell>{data.emp_Name}</TableCell>
                  <TableCell>{data.working_hours}</TableCell>
                  <TableCell>{data.extra_hours}</TableCell>
                  <TableCell>{data.title_code}</TableCell>
                  <TableCell>{data.action_status}</TableCell>
                  <TableCell>
                    <Button icon="edit" basic color="yellow" onClick={() => handleEdit(data)} />
                  </TableCell>
                </TableRow>
              ))
            ) : loading ? (
              <TableRow>
                <TableCell colSpan="10" textAlign="center">
                  <Loader active inline="centered">Loading</Loader>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell colSpan="10" textAlign="center">No data found.</TableCell>
              </TableRow>
            )}
          </TableBody>

          <TableFooter>
            <Table.Row>
              <Table.HeaderCell colSpan="5">Total</Table.HeaderCell>
              <Table.HeaderCell>{totalWorkingHours}</Table.HeaderCell>
              <Table.HeaderCell>{totalExtraHours}</Table.HeaderCell>
              <Table.HeaderCell colSpan="3" />
            </Table.Row>
          </TableFooter>
        </Table>
      </div>
    </div>
  );
};

export default ManualHoursAdd;
